import React, { type FC } from "react";
import { Schema } from "~/foundation/Tracking/Schema";
import { type ItemReference, type RouteData, type SitecoreContextValue } from "~/foundation/Jss";
import { type MagazineArticlePage } from "../generated-types";
import { type Author } from "~/feature/Article/generated-types";
import { type Tag } from "~/foundation/SitecoreContent/generated-types";

type MagazineArticleSchemaProps = {
	route: RouteData<MagazineArticlePage>;
	context: SitecoreContextValue;
	resolveImageUrl: (imagePath?: string) => string | null;
}

export const MagazineArticleSchema: FC<MagazineArticleSchemaProps> = ({ route, context, resolveImageUrl }) => {

	const author = (route.fields?.author as ItemReference<Author> | undefined);
	const authorName = author?.fields?.authorName?.value || author?.name || "";

	return (
		<Schema schema={{
			"@context": "https://schema.org",
			"@type": "WebPage",
			"@id": `${context.custom.absoluteUrl}#WebPage`,
			url: context.custom.absoluteUrl,
			name: route.fields?.pageTitle?.value || route.fields?.metaTitle?.value || route.fields?.title?.value || "Article",
			description: route.fields?.metaDescription?.value || route.fields?.teaser?.value || "Article",
			...(resolveImageUrl(route.fields?.image?.value?.src) && { image: resolveImageUrl(route.fields?.image?.value?.src) }),
			breadcrumb: {
				"@type": "BreadcrumbList",
				itemListElement: context.custom.breadcrumbItems.items.slice(1).map((item, index) => ({
					"@type": "ListItem",
					position: index + 1,
					item: {
						name: item.title,
						"@id": `${item.url || context.custom.absoluteUrl}`
					}
				}))
			},
			mainEntity: {
				"@type": "Article",
				"@id": `${context.custom.absoluteUrl}#Article`,
				headline: route.fields?.title?.value,
				articleBody: route.fields?.textContent?.value,
				image: [
					route.fields?.image?.value?.src || ""
				],
				datePublished: route.fields?.publishDate?.value,
				about: route.fields?.topicTags?.map((topic: ItemReference<Tag>) => ({
					"@type": "DefinedTerm",
					"@id": `${context.custom.hostname}${context.custom.settings.searchResultPageUrl}?topics=${topic!.name}#DefinedTerm`,
					"name": topic?.fields.title?.value || topic!.name,
					"url": `${context.custom.hostname}${context.custom.settings.searchResultPageUrl}?topics=${topic!.name}`
				})) || [],
				...(authorName && {
					author: {
						"@type": "Person",
						name: authorName
					}
				})
			},
			isPartOf: {
				"@type": "Website",
				"@id": `${context.custom.hostname}#Website`,
				name: "Aramco",
				url: context.custom.hostname,
				publisher: {
					"@type": "Corporation",
					"@id": `${context.custom.hostname}#Corporation`,
				}
			}
		}} />
	);
}